import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import { handleSuccessToast } from 'src/components/molecules/Toast';
import { GetCategoryConfigCategoriesQuery } from 'src/graphql/generated';
import { getInitials } from 'src/utils/getInitials';
import { DateFormats, formatDate } from 'src/utils/time';
import { useCategoryTableData } from '../hooks/useCategoryTableData';
import { CategoryEmptyState } from '../molecules/CategoryEmptyState';
import { CategoryTablePagination } from '../molecules/CategoryTablePagination';
import { CategoryTableToolbar } from '../molecules/CategoryTableToolbar';
import { EditCoiCategoryDrawer } from './EditCoiCategoryDrawer';

type Category = NonNullable<
  GetCategoryConfigCategoriesQuery['getOrganisationCategories']
>[number];

const DEFAULT_LOCALE = 'en-GB';

interface CoiCategoryTableProps {
  onCategoryClick?: (category: Category) => void;
  onAddCategory?: () => void;
  forceStatusFilter?: string;
  onForceStatusFilterConsumed?: () => void;
}

export const CoiCategoryTable: React.FC<CoiCategoryTableProps> = ({
  onCategoryClick,
  onAddCategory,
  forceStatusFilter,
  onForceStatusFilterConsumed,
}) => {
  const { tokens, presets } = useTheme<LensTheme>();
  const { getAvatarProps } = presets.AvatarPresets;
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null,
  );

  const {
    rows,
    totalCount,
    isEmpty,
    page,
    rowsPerPage,
    statusFilter,
    isLoading,
    setPage,
    setRowsPerPage,
    setSearchTerm,
    setStatusFilter,
    setStatusFilterDirect,
    handleReorder,
  } = useCategoryTableData(true);

  useEffect(() => {
    if (forceStatusFilter) {
      setStatusFilterDirect(
        forceStatusFilter as 'Active' | 'Form required' | 'Inactive',
      );
      onForceStatusFilterConsumed?.();
    }
  }, [forceStatusFilter, setStatusFilterDirect, onForceStatusFilterConsumed]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const sourceIndex = (page - 1) * rowsPerPage + result.source.index;
    const destIndex = (page - 1) * rowsPerPage + result.destination.index;
    handleReorder(sourceIndex, destIndex);
  };

  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', padding: '48px' }}
        data-testid="coi-category-table-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isEmpty) {
    return <CategoryEmptyState onAddCategory={onAddCategory} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.core.spacing[3].value,
      }}
      data-testid="coi-category-table"
    >
      <CategoryTableToolbar
        onSearch={setSearchTerm}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        isCOI
      />

      <TableContainer sx={{ position: 'relative', overflowX: 'auto' }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: tokens.semantic.color.surface.variant.value,
                }}
              >
                <TableCell sx={{ width: '80px' }}>Order</TableCell>
                <TableCell>Category name</TableCell>
                <TableCell>Created by</TableCell>
                <TableCell>Created at</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell sx={{ width: '90px', borderRadius: 0 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <Droppable droppableId="coi-category-table">
              {(provided) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        align="center"
                        sx={{ padding: '32px' }}
                      >
                        <Typography
                          sx={{
                            color: tokens.semantic.color.type.muted.value,
                          }}
                        >
                          No categories found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((category, index) => (
                      <Draggable
                        key={category.id}
                        draggableId={String(category.id)}
                        index={index}
                      >
                        {(dragProvided, snapshot) => (
                          <TableRow
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            data-testid={`coi-category-row-${category.id}`}
                            onClick={() => onCategoryClick?.(category)}
                            hover
                            sx={{
                              cursor: 'pointer',
                              ...(snapshot.isDragging && {
                                backgroundColor:
                                  tokens.semantic.color.surface.variant.value,
                                boxShadow: 2,
                                display: 'table',
                                tableLayout: 'fixed',
                                width: '100%',
                              }),
                            }}
                          >
                            <TableCell sx={{ width: '80px' }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <span
                                  {...dragProvided.dragHandleProps}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ display: 'flex', cursor: 'grab' }}
                                  data-testid={`coi-category-drag-handle-${category.id}`}
                                >
                                  <i
                                    className="uil uil-draggabledots"
                                    style={{
                                      color:
                                        tokens.semantic.color.type.muted.value,
                                    }}
                                  />
                                </span>
                                <span>{category.order + 1}.</span>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Tooltip
                                title={
                                  category.locales?.[DEFAULT_LOCALE]?.name ??
                                  'Untitled'
                                }
                              >
                                <Link
                                  component="span"
                                  underline="always"
                                  sx={{
                                    color:
                                      tokens.semantic.color.type.default.value,
                                    textAlign: 'left',
                                    fontSize: 'inherit',
                                    fontWeight:
                                      tokens.semantic.fontWeight.emphasis.value,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '280px',
                                    display: 'inline-block',
                                  }}
                                  data-testid={`coi-category-name-${category.id}`}
                                >
                                  {category.locales?.[DEFAULT_LOCALE]?.name ??
                                    'Untitled'}
                                </Link>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const name =
                                  category.Author?.displayName ??
                                  category.Author?.name;
                                if (!name) return <>—</>;
                                return (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                    }}
                                  >
                                    <Avatar
                                      {...getAvatarProps({
                                        size: 'medium',
                                        uniqueId: name,
                                      })}
                                      aria-label={name}
                                    >
                                      {getInitials(name)}
                                    </Avatar>
                                    {name}
                                  </Box>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              {formatDate(
                                category.createdAt,
                                DateFormats.YYYY_MM_DD_TIME_SHORT,
                                false,
                              )}
                            </TableCell>
                            <TableCell>
                              {formatDate(
                                category.updatedAt,
                                DateFormats.YYYY_MM_DD_TIME_SHORT,
                                false,
                              )}
                            </TableCell>
                            <TableCell sx={{ width: '90px' }}>
                              <IconButton
                                size="small"
                                data-testid={`coi-category-edit-${category.id}`}
                                aria-label="Edit category"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedCategory(category);
                                }}
                              >
                                <i className="uil uil-pen" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </DragDropContext>
      </TableContainer>

      <CategoryTablePagination
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />

      {selectedCategory && (
        <EditCoiCategoryDrawer
          category={selectedCategory}
          isOpen={!!selectedCategory}
          onClose={() => setSelectedCategory(null)}
          onSuccess={() =>
            handleSuccessToast({
              toastMessage: 'Saving has been successful',
            })
          }
        />
      )}
    </Box>
  );
};
